import { Plugin } from 'ckeditor5/src/core';
import {
	ButtonView,
	ContextualBalloon,
	clickOutsideHandler,
} from 'ckeditor5/src/ui';
import { icons } from 'ckeditor5/src/core';
import type { Editor } from 'ckeditor5/src/core';
import SmartButtonDialogView from './smartbuttondialogview';
import type { SmartButtonCommandValue } from './smartbuttoncommand';
import SmartButtonCommand from './smartbuttoncommand';
import '../../theme/smartbutton.css';
export default class SmartButtonUI extends Plugin {
	/**
	 * @inheritDoc
	 */
	public static get pluginName() {
		return 'SmartButtonUI' as const;
	}

	/**
	 * @inheritDoc
	 */
	public static get requires() {
		return [ContextualBalloon] as const;
	}

	/**
	 * The contextual balloon plugin instance.
	 */
	private _balloon!: ContextualBalloon;

	/**
	 * The smart button dialog view.
	 */
	private _dialogView!: SmartButtonDialogView;

	/**
	 * @inheritDoc
	 */
	public init(): void {
		const editor = this.editor;
		const t = editor.t;

		// Create the dialog view
		this._dialogView = new SmartButtonDialogView(editor.locale);

		// Get the balloon
		this._balloon = editor.plugins.get(ContextualBalloon);

		// Register the smart button button
		editor.ui.componentFactory.add('smartButton', (locale) => {
			const button = new ButtonView(locale);

			button.set({
				label: t('Smart Button'),
				icon: icons.plus, // A more button-like icon
				tooltip: true,
			});

			this._registerEventHandlers();

			button.on('execute', () => {
				this._showDialog();
			});

			return button;
		});
	}

	/**
	 * Registers event handlers for the dialog.
	 */
	private _registerEventHandlers(): void {
		// Handle form submit
		this._dialogView.on('submit', () => {
			const { text, url, target, alignment } = this._dialogView;

			this.editor.execute(SmartButtonCommand.COMMAND_NAME, {
				text,
				url,
				target,
				alignment,
			});

			this._hideDialog();
		});

		// Handle form cancel
		this._dialogView.on('cancel', () => {
			this._hideDialog();
		});

		// Close on click outside
		clickOutsideHandler({
			emitter: this._dialogView,
			activator: () => this._isDialogInPanel,
			contextElements: [this._balloon.view.element!],
			callback: () => this._hideDialog(),
		});
	}

	/**
	 * Shows the smart button dialog.
	 */
	private _showDialog(): void {
		const command = this.editor.commands.get(
			SmartButtonCommand.COMMAND_NAME
		) as SmartButtonCommand;

		// Check if we're editing an existing button
		if (command && command.value) {
			// Use our defined type
			const commandValue = command.value as SmartButtonCommandValue;

			// Set dialog values from the existing button
			this._dialogView.text = commandValue.text || '';
			this._dialogView.url = commandValue.url || '';
			this._dialogView.target = commandValue.target || '_self';
			this._dialogView.alignment = commandValue.alignment || 'none';
		} else {
			// Reset form values for a new button
			this._dialogView.text = '';
			this._dialogView.url = '';
			this._dialogView.target = '_blank';
			this._dialogView.alignment = 'none';
		}

		this._balloon.add({
			view: this._dialogView,
			position: this._getBalloonPositionData(),
		});

		this._dialogView.focus();
	}

	/**
	 * Hides the smart button dialog.
	 */
	private _hideDialog(): void {
		if (this._isDialogInPanel) {
			this._balloon.remove(this._dialogView);
			this.editor.editing.view.focus();
		}
	}

	/**
	 * Returns true when the dialog is in the balloon panel.
	 */
	private get _isDialogInPanel(): boolean {
		return this._balloon.hasView(this._dialogView);
	}

	/**
	 * Gets the position data for the balloon.
	 */
	private _getBalloonPositionData(): object {
		const view = this.editor.editing.view;
		const viewDocument = view.document;
		const editableElement = viewDocument.selection.editableElement;

		// If there's no editable element, position relative to the editor UI
		if (!editableElement) {
			return {
				target: this.editor.ui.getEditableElement(),
			};
		}

		// Position relative to the current selection
		const range = viewDocument.selection.getFirstRange();
		if (range) {
			return {
				target: view.domConverter.viewRangeToDom(range),
			};
		}

		// Fallback - position relative to the editable element
		return {
			target: view.domConverter.mapViewToDom(editableElement),
		};
	}
}
